import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import { IRootState } from 'Scripts/store';

import {
    useAddDiscountMutation,
    useAddToCartMutation,
    useGetCartQuery,
    useReplaceCartMutation,
    useUpdateCartRowMutation,
} from 'Services/cartApi';
import { type VariantStock } from 'Services/productSearchApi';
import { handleCheckoutClickDatalayerPush } from 'Utils/dataLayerPushService';
import useTrapFocus from 'Hooks/useTrapFocus';

import CtaButton from 'Components/CtaButton';
import CartItemRow from 'Components/CustomerServiceCart/CartItemRow';
import CartItemSummary from 'Components/CustomerServiceCart/CartItemSummary';
import ProductSearch from 'Components/CustomerServiceCart/ProductSearch';
import Loader from 'Components/Loader';

import CloseIcon from 'Icons/closeThin.svg?react';

interface CustomerServiceCartProps {
    isOpen: boolean;
    setIsOpen: (state: boolean) => void;
}

export interface CartVariantData {
    baseId: string;
    id: string;
    size: string;
    color: string;
    colorName: string;
    stock: CartVariantDataStock;
}

interface CartVariantDataStock extends VariantStock {
    isOutOfStock: boolean;
}

interface RowSpecificSearchResult {
    variants: CartVariantData[];
    selected: string;
    rowId: string;
}

interface RowSpecificVariantSelects {
    [rowId: string]: RowSpecificVariantSelect;
}

export interface RowSpecificVariantSelect {
    selectedColor: VariantSelectOption;
    selectedSize: VariantSelectOption;
    colors: VariantSelectOption[];
    sizes: VariantSelectOption[];
    purchaseOrders: VariantPurchaseOrders[];
    quantity: number;
    overbooked: boolean;
}

interface VariantPurchaseOrders {
    deliveryDate: string;
    quantityInPurchaseOrder: number;
}

interface VariantSelectOption {
    label: string;
    value: string;
    outOfStock: boolean;
    quantity: number;
}

interface ReturnFocus {
    type: string;
    id: string;
}

const CustomerServiceCart = ({
    isOpen,
    setIsOpen,
}: CustomerServiceCartProps) => {
    const { shoppingCart, orderDetails, translations, checkoutPage } =
        useSelector((state: IRootState) => state?.cart?.miniCartModel);
    const [returnFocus, setReturnFocus] = useState<ReturnFocus>({
        type: '',
        id: '',
    });
    const [loweredQuantityForVariant, setLoweredQuantityForVariant] =
        useState('');
    const initialized = useRef(false);
    const closeTrap = () => {
        setIsOpen(false);
    };
    const trapRef = useTrapFocus(isOpen, closeTrap);
    const [cartRowVariantData, setCartRowVariantData] =
        useState<RowSpecificVariantSelects>({});
    const [tempVariantData, setTempVariantData] = useState<CartVariantData[]>(
        []
    );
    const [
        updateCartRow,
        {
            isSuccess: isUpdateCartSuccess,
            error: errorCartRow,
            isLoading: isLoadingCartRow,
            isError: isErrorCartRow,
        },
    ] = useUpdateCartRowMutation();
    const [
        addToCart,
        {
            isSuccess: isAddCartSuccess,
            error: errorAddCartRow,
            isLoading: isLoadingAddCartRow,
            isError: isErrorAddCartRow,
        },
    ] = useAddToCartMutation();
    const [
        replaceCartRow,
        {
            isSuccess: isReplaceCartSuccess,
            error: errorReplaceCartRow,
            isLoading: isLoadingReplaceCartRow,
            isError: isErrorReplaceCartRow,
        },
    ] = useReplaceCartMutation();
    const [
        addDiscount,
        {
            isSuccess: isAddDiscountSuccess,
            error: errorAddDiscountRow,
            isLoading: isLoadingAddDiscount,
            isError: isErrorAddDiscountRow,
        },
    ] = useAddDiscountMutation();

    const {
        isLoading: cartIsLoading,
        isError: isErrorCart,
        error: errorCart,
    } = useGetCartQuery();

    const handleCartUpdate = async (
        id: string,
        systemId: string,
        quantity: string,
        type: 'decrement' | 'increment' | 'delete'
    ) => {
        try {
            await updateCartRow({ articleNumber: id, quantity, type }).unwrap();

            const customerServiceDiscount = shoppingCart?.orderItems?.find(
                o => o.systemId === systemId
            )?.discount as {
                amount: number;
                discountType: string;
            };

            if (!customerServiceDiscount) return;

            const shouldUpdatePercentDiscount =
                customerServiceDiscount.discountType === 'percent';

            if (shouldUpdatePercentDiscount) {
                await addDiscount({
                    rowSystemId: systemId,
                    value: customerServiceDiscount.amount,
                    type: 'percent',
                }).unwrap();
            }
        } catch (error) {
            console.error(
                'Error while updating cart and applying discount:',
                error
            );
        }
    };

    const handleCartReplace = (
        id: string,
        quantity: number,
        orderRowId: string,
        type: string,
        loweredQuantity: boolean
    ) => {
        replaceCartRow({
            articleNumber: id,
            quantity,
            orderRowSystemId: orderRowId,
        });
        setReturnFocus({ id, type });
        if (loweredQuantity) {
            setLoweredQuantityForVariant(id);
        }
    };

    const handleUpdateDiscount = (
        id: string,
        discountAmount: number,
        discountType: string
    ) => {
        addDiscount({
            rowSystemId: id,
            value: discountAmount,
            type: discountType,
        });
    };

    const handleAddToCart = (id: string, variantData: CartVariantData[]) => {
        setTempVariantData(variantData);
        addToCart({ articleNumber: id, quantity: 1 });
    };

    const computeVariantOptions = (
        data: RowSpecificSearchResult[]
    ): RowSpecificVariantSelects =>
        data.reduce((acc, { variants, selected, rowId }) => {
            const selectedVariant =
                variants.find(v => v.id === selected) ||
                variants.find(v => !isOutOfStock(v.stock));
            const sizeToColor = new Map<string, Set<string>>();
            const colorToSize = new Map<string, Set<string>>();
            const sizeAvailability = new Map<
                string,
                { id: string; stock: number }
            >();
            const colorAvailability = new Map<
                string,
                { id: string; stock: number }
            >();

            const calculateStock = (stock: CartVariantDataStock) =>
                stock.quantityInStock +
                stock.overbookedQuantity +
                stock.purchaseOrders.reduce(
                    (acc, curr) => acc + curr.quantityInPurchaseOrder,
                    0
                );

            const isOutOfStock = (stock: CartVariantDataStock) =>
                calculateStock(stock) <= 0;

            variants.forEach(({ color, size, id, stock }) => {
                if (!sizeToColor.has(size)) sizeToColor.set(size, new Set());
                sizeToColor.get(size)!.add(color);

                if (!colorToSize.has(color)) colorToSize.set(color, new Set());
                colorToSize.get(color)!.add(size);

                if (!isOutOfStock(stock)) {
                    if (!sizeAvailability.has(size))
                        sizeAvailability.set(size, {
                            id,
                            stock: calculateStock(stock),
                        });
                    if (!colorAvailability.has(color))
                        colorAvailability.set(color, {
                            id,
                            stock: calculateStock(stock),
                        });
                }
            });

            const sizes = Array.from(sizeToColor.keys()).map(size => {
                const variant = variants.find(
                    v => v.size === size && v.color === selectedVariant.color
                );
                let variantId = variant?.id || '';
                let variantStock = variant ? calculateStock(variant.stock) : 0;

                if (!variant || isOutOfStock(variant.stock)) {
                    const availableVariant = sizeAvailability.get(size);
                    if (availableVariant) {
                        variantId = availableVariant.id;
                        variantStock = availableVariant.stock;
                    }
                }

                return {
                    value: variantId,
                    label: size,
                    outOfStock: !sizeAvailability.get(size),
                    quantity: variantStock,
                };
            });

            const colors = Array.from(colorToSize.keys()).map(color => {
                const variant = variants.find(
                    v => v.color === color && v.size === selectedVariant.size
                );
                let variantId = variant?.id || '';
                let variantStock = variant ? calculateStock(variant.stock) : 0;

                if (!variant || isOutOfStock(variant.stock)) {
                    const availableVariant = colorAvailability.get(color);
                    if (availableVariant) {
                        variantId = availableVariant.id;
                        variantStock = availableVariant.stock;
                    }
                }

                const colorName =
                    variants.find(v => v.color === color)?.colorName || '';
                return {
                    value: variantId,
                    label: colorName,
                    outOfStock: !colorAvailability.get(color),
                    quantity: variantStock,
                };
            });

            acc[rowId] = {
                selectedColor: {
                    value: selectedVariant.id,
                    label: selectedVariant.colorName,
                    outOfStock: !colorAvailability.get(selectedVariant.color),
                    quantity: calculateStock(selectedVariant.stock),
                },
                selectedSize: {
                    value: selectedVariant.id,
                    label: selectedVariant.size,
                    outOfStock: !sizeAvailability.get(selectedVariant.size),
                    quantity: calculateStock(selectedVariant.stock),
                },
                colors,
                sizes,
                quantity: selectedVariant.stock.quantityInStock,
                purchaseOrders: selectedVariant.stock.purchaseOrders || [],
                overbooked: selectedVariant.stock.overbookedQuantity > 0,
            };

            return acc;
        }, {});

    const removeVariantData = () => {
        if (!shoppingCart?.orderItems?.length) {
            localStorage.removeItem('gudrunCartVariantData');
            return;
        }

        const storedData = localStorage.getItem('gudrunCartVariantData');
        if (!storedData) return;

        const parsed = JSON.parse(storedData);
        const validKeys = new Set(
            shoppingCart.orderItems.map(row => row.baseId)
        );

        Object.keys(parsed).forEach(key => {
            if (!validKeys.has(key)) {
                delete parsed[key];
            }
        });

        localStorage.setItem('gudrunCartVariantData', JSON.stringify(parsed));
    };

    const saveSearchResult = (data: CartVariantData[]) => {
        const storedData = localStorage.getItem('gudrunCartVariantData');
        const newData = {
            ...(storedData ? JSON.parse(storedData) : {}),
            [data[0].baseId]: data,
        };

        localStorage.setItem('gudrunCartVariantData', JSON.stringify(newData));
    };

    const getRowSpecificSearchResult = (): RowSpecificSearchResult[] => {
        const storedData = localStorage.getItem('gudrunCartVariantData');
        if (!storedData || !shoppingCart?.orderItems?.length) return [];

        const data = JSON.parse(storedData);
        return shoppingCart.orderItems.map(o => {
            const rowData = data[o.baseId as keyof RowSpecificVariantSelects];

            return {
                variants: rowData,
                selected: o.id,
                rowId: o.systemId as keyof RowSpecificVariantSelects,
            };
        });
    };

    useEffect(() => {
        if (isErrorCartRow && 'data' in errorCartRow) {
            const errMessage: { title?: string } = errorCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCartRow, errorCartRow]);

    useEffect(() => {
        if (isErrorAddCartRow && 'data' in errorAddCartRow) {
            const errMessage: { title?: string } = errorAddCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorAddCartRow, errorAddCartRow]);

    useEffect(() => {
        if (isErrorReplaceCartRow && 'data' in errorReplaceCartRow) {
            const errMessage: { title?: string } = errorReplaceCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorReplaceCartRow, errorReplaceCartRow]);

    useEffect(() => {
        if (isErrorCart && 'data' in errorCart) {
            const errMessage: { title?: string } = errorCart.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCart, errorCart]);

    useEffect(() => {
        if (shoppingCart && !initialized.current) {
            initialized.current = true;
            setCartRowVariantData(
                computeVariantOptions(getRowSpecificSearchResult())
            );
        }
    }, [shoppingCart]);

    useEffect(() => {
        if (isAddCartSuccess) {
            saveSearchResult(tempVariantData);
            setCartRowVariantData(
                computeVariantOptions(getRowSpecificSearchResult())
            );
        }
    }, [isAddCartSuccess]);

    useEffect(() => {
        if (isUpdateCartSuccess) {
            removeVariantData();
        }
    }, [isUpdateCartSuccess]);

    useEffect(() => {
        if (isReplaceCartSuccess) {
            setCartRowVariantData(
                computeVariantOptions(getRowSpecificSearchResult())
            );
            setTimeout(() => {
                setLoweredQuantityForVariant('');
            }, 100);
        }
    }, [isReplaceCartSuccess]);

    return (
        <section
            ref={trapRef}
            className="flex h-[100dvh] w-full flex-col bg-white shadow-lg min-[550px]:w-[530px] min-[550px]:max-w-[530px] min-[1000px]:w-[700px] min-[1000px]:max-w-[700px]"
        >
            {cartIsLoading && (
                <div className="flex h-full flex-1 flex-col justify-center">
                    <Loader />
                </div>
            )}
            {!cartIsLoading && (
                <>
                    <div className="relative shadow-[0px_5px_16px_0px_rgba(0,0,0,0.08)]">
                        <div className="flex items-center justify-between px-7 py-4">
                            <h3 className="my text-l flex flex-wrap font-montserrat font-semibold">
                                {translations.miniCartTitle} (
                                {shoppingCart.totalQuantity}
                                {shoppingCart.totalQuantity !== 1
                                    ? ` ${translations.cartTitleAdditionalInfoPlural}`
                                    : ` ${translations.cartTitleAdditionalInfoSingular}`}
                                )
                            </h3>
                            <button
                                className="cursor-pointer"
                                onClick={() => setIsOpen(false)}
                            >
                                <CloseIcon className="h-5 w-5" />
                                <span className="sr-only">
                                    {translations.minicartClose}
                                </span>
                            </button>
                        </div>
                        <div>
                            <ProductSearch addToCart={handleAddToCart} />
                        </div>
                        {shoppingCart.totalQuantity > 0 && (
                            <div className="flex bg-[#F8F5F0] text-xs">
                                <span className="grow py-1.5 pl-4">
                                    {translations.customerServiceCartProduct}
                                </span>
                                <span className="w-44 py-1.5 pl-4">
                                    {translations.customerServiceCartVariant}
                                </span>
                                <span className="w-28 py-1.5 pl-4">
                                    {translations.cartPriceLabel}
                                </span>
                                <span className="w-20 py-1.5 pr-4 text-right">
                                    {translations.cartTotalPriceLabel}
                                </span>
                            </div>
                        )}
                    </div>
                    {shoppingCart.totalQuantity > 0 ? (
                        <ul
                            className={`scroll-bar-gsd::-webkit-scrollbar scroll-bar-gsd::-webkit-scrollbar-thumb scroll-bar-gsd flex grow flex-col gap-1 overflow-y-auto bg-[#F8F5F0] transition-opacity duration-layout ${
                                isLoadingCartRow || isLoadingAddCartRow
                                    ? 'opacity-50'
                                    : 'opacity-100'
                            }`}
                        >
                            {shoppingCart?.orderItems.map(item => (
                                <CartItemRow
                                    key={`row-${item.systemId}`}
                                    updateCartRow={handleCartUpdate}
                                    replaceCartRow={handleCartReplace}
                                    updateDiscount={handleUpdateDiscount}
                                    orderItem={item}
                                    variantSelectData={
                                        cartRowVariantData[
                                            item.systemId as keyof RowSpecificVariantSelects
                                        ]
                                    }
                                    translations={translations}
                                    returnFocus={
                                        returnFocus.id === item.id
                                            ? returnFocus.type
                                            : ''
                                    }
                                    showLoweredQuantityWarning={
                                        loweredQuantityForVariant === item.id
                                    }
                                />
                            ))}
                        </ul>
                    ) : (
                        <div className="flex grow items-center justify-center bg-[#F8F5F0]">
                            {translations.customerServiceCartEmpty}
                        </div>
                    )}
                    <div className="w-full bg-white px-4.5 py-2.5 shadow-[0px_-5px_16px_0px_rgba(0,0,0,0.05)]">
                        <CartItemSummary
                            isLoading={isLoadingCartRow || isLoadingAddCartRow}
                            items={orderDetails}
                            translations={translations}
                        />
                        <CtaButton
                            as="a"
                            ctaType="primary"
                            className="checkout-button w-full text-center font-semibold"
                            href={checkoutPage.href}
                            onClick={() => handleCheckoutClickDatalayerPush()}
                        >
                            {checkoutPage.text}
                        </CtaButton>
                    </div>
                </>
            )}
        </section>
    );
};

export default CustomerServiceCart;
